
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DeadlineInput } from "./deadline-timer/DeadlineInput";
import { useDeadlineTimer } from "./deadline-timer/useDeadlineTimer";
import { InfoIcon } from "lucide-react";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipTrigger,
  TooltipProvider 
} from "@/components/ui/tooltip";
import { trackEvent } from "@/integrations/posthog/client";
import { addDays } from "date-fns";

interface DeadlineTimerSectionProps {
  eventDeadline: Date | null;
  onDeadlineChange: (deadline: Date | null) => void;
}

export const DeadlineTimerSection = ({
  eventDeadline,
  onDeadlineChange
}: DeadlineTimerSectionProps) => {
  const {
    enabled,
    timeValue,
    timeUnit,
    inputError,
    handleSwitchChange,
    handleTimeChange,
    handleInputBlur,
    handleUnitChange,
    eventDeadline: calculatedDeadline
  } = useDeadlineTimer({
    initialDeadline: eventDeadline,
    onDeadlineChange
  });
  
  const handleToggleChange = (checked: boolean) => {
    handleSwitchChange(checked);
    
    if (checked) {
      // Track deadline enabled
      trackEvent('deadline_timer_enabled', {
        default_value: timeValue,
        unit: timeUnit
      });
    } else {
      // Track deadline disabled
      trackEvent('deadline_timer_disabled');
    }
  };
  
  const handleDeadlineChange = (deadline: Date) => {
    onDeadlineChange(deadline);
    
    // Track deadline changed
    trackEvent('deadline_timer_updated', {
      days_from_now: Math.round((deadline.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    });
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Label htmlFor="deadline-toggle" className="font-medium">Set Response Deadline</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[200px]">
                <p>Set a deadline for when you need responses from your guests</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Switch
          id="deadline-toggle"
          checked={enabled}
          onCheckedChange={handleToggleChange}
        />
      </div>
      
      <DeadlineInput
        enabled={enabled}
        timeValue={timeValue}
        timeUnit={timeUnit}
        inputError={inputError}
        eventDeadline={calculatedDeadline}
        onTimeChange={handleTimeChange}
        onInputBlur={handleInputBlur}
        onUnitChange={handleUnitChange}
      />
    </div>
  );
};
